import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ASSETS, ORG } from "@/lib/constants";
import { InterestDialog } from "@/components/dialogs/InterestDialog";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1] } },
};

export function Hero() {
  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden grain">
      <div className="absolute inset-0">
        <img src={ASSETS.boysTeam} alt="CT Blazers championship team celebrating" className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="absolute inset-0 court-glow" />
      </div>

      <img
        src={ASSETS.logo}
        alt=""
        aria-hidden
        className="hidden xl:block absolute right-6 top-1/2 -translate-y-1/2 w-[420px] opacity-[0.06] animate-float pointer-events-none"
      />

      <motion.div variants={container} initial="hidden" animate="show" className="container-x relative z-10 pt-28 pb-16">
        <motion.div variants={item}>
          <span className="inline-flex items-center gap-2 rounded-full border border-blaze-orange/40 bg-blaze-orange/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-blaze-orange">
            <Flame size={14} /> Norwalk's Competitive AAU Program
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="mt-6 font-display font-black uppercase leading-[0.88] tracking-tight text-white text-6xl sm:text-7xl lg:text-8xl"
        >
          Build. <span className="text-gradient-blaze">Compete.</span> <br className="hidden sm:block" />
          Develop.
        </motion.h1>

        <motion.p variants={item} className="mt-5 font-heading text-3xl sm:text-4xl tracking-wide text-white">
          The CT <span className="text-blaze-blueLight">BLAZERS</span> <span className="text-blaze-orange">WAY</span>
        </motion.p>

        <motion.p variants={item} className="mt-5 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed">
          CT BLAZERS is a competitive AAU basketball program focused on player development, teaching the game the right
          way, and creating a family-friendly environment where every player is challenged to get better.
        </motion.p>

        <motion.div variants={item} className="mt-9 flex flex-wrap items-center gap-4">
          <Button
            asChild
            data-testid="hero-explore-btn"
            className="bg-blaze-blue hover:bg-blaze-blueLight text-white font-bold uppercase tracking-wide rounded-full h-13 px-8 py-6 text-base shadow-[0_10px_30px_rgba(37,99,235,0.4)] transition-all hover:-translate-y-1"
          >
            <Link to="/#programs">
              Explore Our Programs <ArrowRight size={18} className="ml-1" />
            </Link>
          </Button>
          <InterestDialog category="join" program="Join CT BLAZERS" testId="hero-join">
            <Button
              data-testid="hero-join-btn"
              className="bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-13 px-8 py-6 text-base shadow-[0_10px_30px_rgba(246,140,31,0.4)] transition-all hover:-translate-y-1"
            >
              Join CT Blazers
            </Button>
          </InterestDialog>
        </motion.div>

        <motion.div
          variants={item}
          data-testid="hero-location"
          className="mt-10 inline-flex items-center gap-2.5 rounded-full glass border border-white/10 px-5 py-2.5 text-sm font-semibold text-white/80"
        >
          <MapPin size={16} className="text-blaze-blueLight" />
          {ORG.location}
        </motion.div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="h-10 w-px bg-gradient-to-b from-white/50 to-transparent animate-pulse-glow" />
      </div>
    </section>
  );
}
